import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Alert } from '@mui/material';
import { History as HistoryIcon, Assessment as ReportIcon } from '@mui/icons-material';
import { useNotifications } from '@shared/components/notifications/useNotifications';
import {
  useSurveyAudit,
  useUpdateResponseInclusion,
  useCompleteAndPublishAudit,
} from '../hooks/useSurveyAudit';
import { useAuditProgress, useAuditTrail } from '../hooks/useAuditProgress';
import { useAuditQuestions } from '../hooks/useAuditQuestions';
import { AuditQuestionCard } from '../components/AuditQuestionCard';
import { AuditTrailDialog } from '../components/AuditTrailDialog';
import { CompleteAuditDialog } from '../components/CompleteAuditDialog';
import { GenerateReportButton } from '../components/GenerateReportButton';
import { ViewReportButton } from '../components/ViewReportButton';
import { AuditViewerSkeleton } from '../components/skeletons';
import { QuestionWithResponsesForAudit, AuditUIState, AuditProgressDisplay } from '../types';
import { useSurveyAuditDisplayData } from '../hooks/useSurveyAuditDisplayData';
import { AUDIT_EVENTS } from '../../../utils/trackingEvents';
import { usePostHog } from '../../../hooks/usePostHog';
import { useAuditBreadcrumb } from '../hooks/useAuditBreadcrumb';
import { useSetPageActions } from '../../../contexts/PageActionsContext';

export const SurveyAuditPage: React.FC = () => {
  const { auditId } = useParams<{ auditId: string }>();
  const navigate = useNavigate();
  const { captureEvent } = usePostHog();
  const { setActions } = useSetPageActions();
  useNotifications();

  // Set breadcrumb metadata
  useAuditBreadcrumb(auditId);

  // UI State
  const [uiState, setUiState] = useState<AuditUIState>({
    currentQuestionIndex: 0,
    isLoading: false,
    isSaving: false,
    showAuditTrail: false,
    showCompleteDialog: false,
    showPublishDialog: false,
    bulkSelectionMode: false,
    selectedResponseIds: new Set(),
  });

  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());

  // API Hooks
  const {
    data: auditData,
    isLoading: isLoadingAudit,
    error: auditError,
  } = useSurveyAudit(auditId || '');
  const { data: progressData } = useAuditProgress(auditId || '');
  const { data: questionsData, isLoading: isLoadingQuestions } = useAuditQuestions(auditId || '');
  const { data: trailData, isLoading: isLoadingTrail } = useAuditTrail(auditId || '');

  // Mutations
  const updateResponseInclusion = useUpdateResponseInclusion();
  const completeAndPublishAudit = useCompleteAndPublishAudit();

  // Memoized data processing
  const audit = auditData?.data;
  const progress = progressData?.data;
  const questions = questionsData?.data;
  const trail = trailData?.data?.entries || [];

  // Get display data for the audit
  const auditDisplayData = useSurveyAuditDisplayData(audit);

  const progressDisplay: AuditProgressDisplay = useMemo(() => {
    if (!progress) {
      return {
        percentage: 0,
        reviewedQuestions: 0,
        totalQuestions: 0,
        includedResponses: 0,
        totalResponses: 0,
        canComplete: false,
        canPublish: false,
      };
    }

    return {
      percentage: progress.progress.percentageComplete ?? 0,
      reviewedQuestions: progress.progress.reviewedQuestions ?? 0,
      totalQuestions: progress.progress.totalQuestions ?? 0,
      includedResponses: progress.progress.includedResponses ?? 0,
      totalResponses: progress.progress.totalResponses ?? 0,
      canComplete: progress.canComplete ?? false,
      canPublish: progress.canPublish ?? false,
    };
  }, [progress]);

  // Register audit trail action button in breadcrumb bar
  useEffect(() => {
    if (audit) {
      setActions([
        {
          id: 'audit-trail',
          type: 'button',
          label: 'Audit Trail',
          icon: <HistoryIcon />,
          variant: 'outlined',
          size: 'small',
          onClick: () => {
            captureEvent(AUDIT_EVENTS.AUDIT_TRAIL_VIEWED, {
              audit_id: audit.id,
              audit_status: audit.status,
              survey_id: audit.surveyId,
              survey_instance_id: audit.surveyInstanceId,
              audited_by: audit.auditedBy,
              trail_entries_count: trail.length,
              questions_reviewed: audit.questionInclusions?.length || 0,
              opened_from: 'breadcrumb_bar',
            });
            setUiState((prev) => ({ ...prev, showAuditTrail: true }));
          },
        },
      ]);
    }
  }, [audit, trail.length, captureEvent, setActions]);

  // Track audit page view when component mounts and audit data is loaded
  useEffect(() => {
    if (auditId && audit && auditDisplayData && !auditDisplayData.surveyTitleLoading) {
      const eventType =
        audit.status === 'IN_PROGRESS' ? AUDIT_EVENTS.AUDIT_CONTINUED : AUDIT_EVENTS.AUDIT_VIEWED;

      captureEvent(eventType, {
        audit_id: auditId,
        audit_status: audit.status,
        survey_id: audit.surveyId,
        survey_instance_id: audit.surveyInstanceId,
        survey_title: auditDisplayData.surveyTitle,
        audited_by: audit.auditedBy,
        tenant_id: audit.tenantId,
        domain_id: audit.domainId,
        subject_id: audit.subjectId,
        is_locked: audit.isLocked,
        questions_reviewed: audit.questionInclusions?.length || 0,
        total_responses_included:
          audit.questionInclusions?.reduce(
            (total: number, q: any) => total + (q.includedResponseIds?.length || 0),
            0
          ) || 0,
        total_responses_excluded:
          audit.questionInclusions?.reduce(
            (total: number, q: any) => total + (q.excludedResponseIds?.length || 0),
            0
          ) || 0,
        progress_percentage: progress?.progress.percentageComplete || 0,
        can_complete: progressDisplay.canComplete,
        can_publish: progressDisplay.canPublish,
        started_at: audit.startedAt,
        completed_at: audit.completedAt,
        published_at: audit.publishedAt,
      });
    }
  }, [auditId, audit, auditDisplayData, progress, progressDisplay, captureEvent]);

  // Transform questions data for UI
  const questionsForAudit: QuestionWithResponsesForAudit[] = useMemo(() => {
    if (!questions || !audit) return [];

    return questions.map((question: any) => {
      const questionInclusion = audit.questionInclusions?.find((q) => q.questionId === question.id);
      const includedResponseIds = questionInclusion?.includedResponseIds || [];
      const includedCommentIds = questionInclusion?.includedCommentIds || [];
      const totalResponses = question.responses?.length || 0;
      const includedCount = includedResponseIds.length;
      const excludedCount = totalResponses - includedCount;

      // Handle multilingual text - extract English first, then fallback
      const getDisplayText = (textObj: any): string => {
        if (typeof textObj === 'string') return textObj;
        if (typeof textObj === 'object' && textObj !== null) {
          return (
            textObj.en ||
            textObj.no ||
            textObj.de ||
            Object.values(textObj)[0] ||
            'Untitled Question'
          );
        }
        return 'Untitled Question';
      };

      return {
        questionId: question.id,
        questionText: getDisplayText(question.title || question.text),
        questionDescription: question.description
          ? getDisplayText(question.description)
          : undefined,
        questionType: question.type,
        options: question.options || [],
        responses:
          question.responses?.map((response: any) => ({
            id: response.id,
            questionId: question.id,
            responseValue: response.value,
            comment: response.comment,
            isIncluded: includedResponseIds.includes(response.id),
            isCommentIncluded: includedCommentIds.includes(response.id),
            submittedAt: response.submittedAt,
          })) || [],
        totalResponses,
        includedCount,
        excludedCount,
        isReviewed: questionInclusion !== undefined,
      };
    });
  }, [questions, audit]);

  // Event Handlers
  const handleInclusionChange = async ({
    questionId,
    responseId,
    responseIncluded,
    commentIncluded,
    hasComment,
  }: {
    questionId: string;
    responseId: string;
    responseIncluded: boolean;
    commentIncluded: boolean;
    hasComment: boolean;
  }) => {
    if (!audit) return;

    try {
      await updateResponseInclusion.mutateAsync({
        auditId: audit.id,
        questionId,
        responseId,
        responseIncluded,
        commentIncluded,
        hasComment,
      });
    } catch (err) {
      console.error('Failed to update response inclusion:', err);
    }
  };

  const handleExpandToggle = (questionId: string) => {
    setExpandedQuestions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const handlePublishAudit = async () => {
    if (!audit) return;

    try {
      await completeAndPublishAudit.mutateAsync(audit.id);
      setUiState((prev) => ({
        ...prev,
        showCompleteDialog: false,
        showPublishDialog: false,
      }));
    } catch (err) {
      console.error('Failed to publish audit:', err);
    }
  };

  // Loading and Error States
  if (isLoadingAudit || isLoadingQuestions) {
    return <AuditViewerSkeleton questionCount={4} showHeader={false} showProgressBar={false} />;
  }

  if (auditError || !audit) {
    return (
      <Container maxWidth={false} disableGutters>
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>
            Audit Not Found
          </Typography>
          <Typography>
            The requested audit could not be found or you don&apos;t have permission to access it.
          </Typography>
          <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
            Go Back
          </Button>
        </Alert>
      </Container>
    );
  }

  const isAuditLocked = audit.isLocked || audit.status === 'PUBLISHED';

  return (
    <Box>
      <Container maxWidth={false} disableGutters>
        {/* Banner Spacer Zone — fixed height keeps content aligned regardless of banner visibility */}
        <Box sx={{ minHeight: 72 }}>
          {isAuditLocked && (
            <Alert
              severity="info"
              sx={{
                display: 'flex',
                alignItems: 'center',
                '& .MuiAlert-message': {
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  py: 0,
                },
              }}
            >
              This audit is locked and cannot be modified.
              {audit.status === 'PUBLISHED' && (
                <Box sx={{ ml: 'auto' }}>
                  <ViewReportButton
                    auditId={audit.id}
                    auditStatus={audit.status}
                    surveyTitle={auditDisplayData.surveyTitle}
                    size="small"
                    variant="contained"
                  />
                </Box>
              )}
            </Alert>
          )}
        </Box>

        {/* Questions List */}
        <Box sx={{ mb: 3 }}>
          {questionsForAudit.length === 0 ? (
            <Alert severity="info">No questions found for this audit.</Alert>
          ) : (
            questionsForAudit.map((question, index) => (
              <AuditQuestionCard
                key={question.questionId}
                question={question}
                questionIndex={index}
                totalQuestions={questionsForAudit.length}
                onInclusionChange={handleInclusionChange}
                isExpanded={expandedQuestions.has(question.questionId)}
                onExpandToggle={handleExpandToggle}
                disabled={isAuditLocked}
              />
            ))
          )}
        </Box>

        {/* Static Action Buttons at Bottom */}
        {!isAuditLocked && audit.status === 'IN_PROGRESS' && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<ReportIcon />}
              onClick={() => {
                setUiState((prev) => ({ ...prev, showPublishDialog: true }));
              }}
              disabled={!progressDisplay.canComplete}
            >
              Generate Report
            </Button>
          </Box>
        )}

        {/* Static Report Buttons for Published Audits */}
        {isAuditLocked && audit.status === 'PUBLISHED' && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <GenerateReportButton
              auditId={audit.id}
              auditStatus={audit.status}
              isLocked={audit.isLocked}
              surveyTitle={auditDisplayData.surveyTitle}
              size="medium"
              variant="outlined"
            />
          </Box>
        )}
      </Container>

      {/* Dialogs */}
      <AuditTrailDialog
        open={uiState.showAuditTrail}
        onClose={() => setUiState((prev) => ({ ...prev, showAuditTrail: false }))}
        auditId={audit.id}
        surveyId={audit.surveyId}
        auditTrail={trail}
        questions={questionsForAudit}
        isLoading={isLoadingTrail}
      />

      <CompleteAuditDialog
        open={uiState.showCompleteDialog || uiState.showPublishDialog}
        onClose={() =>
          setUiState((prev) => ({
            ...prev,
            showCompleteDialog: false,
            showPublishDialog: false,
          }))
        }
        auditId={audit.id}
        progress={
          progress?.progress || {
            totalQuestions: progressDisplay.totalQuestions,
            reviewedQuestions: progressDisplay.reviewedQuestions,
            percentageComplete: progressDisplay.percentage,
            totalResponses: progressDisplay.totalResponses,
            includedResponses: progressDisplay.includedResponses,
            excludedResponses: progressDisplay.totalResponses - progressDisplay.includedResponses,
          }
        }
        onPublish={handlePublishAudit}
        isPublishing={completeAndPublishAudit.isPending}
        canPublish={progressDisplay.canComplete}
      />
    </Box>
  );
};
